import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { paths } from "@/api/schema";

/** A mutation names its affected reads. Existing OpenAPI keys remain the contract. */
export type ReadTarget = {
  path: keyof paths;
  params?: Readonly<Record<string, string | number>>;
  descendants?: boolean;
} | { key: QueryKey };

export function matchesRead(key: QueryKey, target: ReadTarget): boolean {
  if ("key" in target) return target.key.every((part, index) => part === key[index]);
  if (key[0] !== "get" || typeof key[1] !== "string") return false;
  if (key[1] !== target.path && !(target.descendants && key[1].startsWith(`${target.path}/`))) return false;
  const init = key[2] as { params?: { path?: Record<string, unknown> } } | undefined;
  return Object.entries(target.params ?? {}).every(([name, value]) => init?.params?.path?.[name] === value);
}

export function projectReads(slug: string, ...routes: (keyof paths)[]): ReadTarget[] {
  return routes.map(path => ({ path, params: { slug }, descendants: true }));
}

export function invalidateReads(client: QueryClient, targets: readonly ReadTarget[]): Promise<void> {
  return client.invalidateQueries({ predicate: query => targets.some(target => matchesRead(query.queryKey, target)) });
}
